import { Box, Grid, Typography } from "@mui/material";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import useClinic from "../../../hooks/useClinic";
import ActionableSearchBar from "../../../components/UI/ActionableSearchBar";
import ProgressionAnalysisCard from "../../../components/UI/ProgressionAnalysisCard";
import ListHeader from "../../../components/UI/ListHeader";
import { deleteProgressionAnalysis } from "../../../services/Exam";
import { itemsRendering, pagesCount } from "../../../utils/paginationHelper";
import PaginationWrapper from "../../../components/UI/PaginationWrapper";
import DeleteProgressionAnalysisModal from "../../../components/UI/DeleteProgressionAnalysisModal";
import { ToastContext } from "../../../contexts/ToastContext";
import SnackbarMessage from "../../../components/UI/SnackbarMessage";

const ProgressionAnalysisList = ({
  fetchedAnalysisList,
  patientId,
  progressAnalysisOnClick = () => {},
  maxNumOfRows = 4,
  patientName,
  hasValidExams = false,
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { clinicId, clinicLicensing } = useClinic();
  const { toast, setToast } = useContext(ToastContext);

  const [progressionAnalysisList, setProgressionAnalysisList] =
    useState(fetchedAnalysisList);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [order, setOrder] = useState("desc");
  const [orderByOptions, setOrderByOptions] = useState(["creationDate"]);

  const headCells = [
    {
      id: "empty",
      label: null,
      gridSize: 0,
      orderUsing: ["eye"],
    },
    {
      id: "eye",
      label: t("exams_column_eye"),
      gridSize: 1.5,
      orderUsing: ["eye"],
    },
    {
      id: "earliestCompletionLocalDate",
      label: t("completion_date_range"),
      gridSize: 6,
      orderUsing: ["earliestCompletionLocalDate"],
    },
    {
      id: "creationDate",
      label: t("creation_date"),
      gridSize: 3.5,
      orderUsing: ["creationDate"],
    },
  ];

  const extraCells = [
    {
      id: "manage",
      label: "",
      gridSize: 1,
    },
  ];

  const onFilterChange = useCallback(
    (event) => {
      if (page > 1) setPage(1);
      setSearchTerm(event.target.value);
    },
    [page]
  );

  // Sorting logic
  const handleRequestSort = (event, property) => {
    const isAsc =
      orderByOptions.length &&
      orderByOptions[0] === property[0] &&
      order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderByOptions(property);
  };

  // Sorting rows
  const sortedRows = useMemo(() => {
    return progressionAnalysisList.slice().sort((a, b) => {
      if (order === "asc") {
        return a[orderByOptions[0]] > b[orderByOptions[0]] ? 1 : -1;
      } else {
        return a[orderByOptions[0]] < b[orderByOptions[0]] ? 1 : -1;
      }
    });
  }, [progressionAnalysisList, order, orderByOptions]);

  // Filtering rows
  const filteredRows = useMemo(() => {
    return sortedRows.filter((progressionAnalysis) => {
      return !(
        searchTerm?.length &&
        !(
          progressionAnalysis?.eye
            ?.toLowerCase()
            ?.includes(searchTerm.toLowerCase()) ||
          progressionAnalysis?.creationDate
            ?.toLowerCase()
            ?.includes(searchTerm.toLowerCase()) ||
          `${progressionAnalysis?.earliestCompletionLocalDate} - ${progressionAnalysis?.latestCompletionLocalDate}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    });
  }, [sortedRows, searchTerm]);

  const rowsToRender = useMemo(
    () => itemsRendering(filteredRows, page, Math.max(maxNumOfRows, 4)),
    [filteredRows, maxNumOfRows, page]
  );

  const handleCheckboxClick = useCallback(
    (id) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
    },
    [selected]
  );

  const isSelected = useCallback(
    (id) => selected.indexOf(id) !== -1,
    [selected]
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = rowsToRender.map((n) => n.id);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [rowsToRender]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteModal(true);
  }, []);

  const handleDeleteClose = useCallback((event, reason) => {
    if (reason !== "backdropClick") {
      setDeleteModal(false);
    }
  }, []);

  const DeleteProgressionAnalysis = useCallback(async () => {
    setDeleteModal(false);

    const generateDeletePromise = async (id) => {
      const token = await getAccessTokenSilently();
      await deleteProgressionAnalysis(token, clinicId, patientId, id);
    };

    // delete from api
    try {
      const deletePromises = selected.map((id) => generateDeletePromise(id));
      await Promise.all(deletePromises);

      setToast(() => ({
        success: true,
        message: t("progression_analysis_delete_success"),
      }));

      const newRows = progressionAnalysisList.filter(
        (progressionAnalysis) => !selected.includes(progressionAnalysis.id)
      );

      setProgressionAnalysisList([...newRows]);

      setSelected([]);
    } catch (error) {
      setToast(() => ({
        success: false,
        message: t("progression_analysis_delete_error"),
      }));
    }
  }, [
    clinicId,
    getAccessTokenSilently,
    patientId,
    progressionAnalysisList,
    selected,
    setToast,
    t,
  ]);

  const clearForm = useCallback(() => {}, []);

  const onPaginationChange = useCallback((_, value) => {
    setPage(value);
  }, []);

  const clearSearchFilter = useCallback(() => {
    setSearchTerm("");
  }, []);

  return (
    <>
      <Grid
        item
        display={"flex"}
        alignItems={"center"}
        xs={12}
        justifyContent={"right"}
        gap={2}
        mb={{ xs: 1, sm: 0 }}
      >
        <ActionableSearchBar
          placeholder={`${t("word_search")}...`}
          onSearchChange={onFilterChange}
          onClear={clearSearchFilter}
          showDeleteButton={selected.length > 0}
          disableDeleteButton={!selected.length}
          deleteButtonOnClick={handleDeleteClick}
          showAddButton={Boolean(
            clinicLicensing?.canUseProgressionReports && hasValidExams
          )}
          addButtonOnClick={() => {
            clearForm();
            progressAnalysisOnClick();
          }}
          selectedRows={selected}
          addTooltip={t("progress_analysis_header")}
          searchTooltip={t("word_search_progression_analyses")}
        />
      </Grid>

      <Grid container mt={{ xs: 0, sm: 3 }}>
        <Grid item xs={12}>
          <ListHeader
            containerPaddingX={0}
            headCells={headCells}
            numSelected={selected.length}
            order={order}
            orderBy={orderByOptions?.[0] ?? "creationDate"}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rowsToRender.length}
            checkBoxGrid={1}
            shouldDisplayGrid="sm"
            displayFirtsBeforeCheckbox
            extraCells={extraCells}
          />
          {rowsToRender.length > 0 ? (
            <Box mt={1}>
              {rowsToRender.map((row) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <ProgressionAnalysisCard
                    key={row?.id}
                    row={row}
                    patientId={patientId}
                    isSelected={isItemSelected}
                    headCells={headCells}
                    handleCheckboxClick={handleCheckboxClick}
                  />
                );
              })}
            </Box>
          ) : (
            <Box
              my={12}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
              gap="20px"
            >
              <Typography fontWeight={600} fontSize="20px">
                {patientName} {t("has_no_progression_analysis")}
              </Typography>
              <Typography fontSize="20px" maxWidth="300px">
                {hasValidExams
                  ? t("click_to_add_progression_analysis")
                  : t("progression_analysis_error")}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {progressionAnalysisList.length > rowsToRender.length && (
        <PaginationWrapper
          page={page}
          count={pagesCount(progressionAnalysisList, Math.max(maxNumOfRows, 4))}
          onChange={onPaginationChange}
        />
      )}

      <DeleteProgressionAnalysisModal
        open={deleteModal}
        onClose={handleDeleteClose}
        onConfirm={DeleteProgressionAnalysis}
        onCancel={handleDeleteClose}
      />

      <SnackbarMessage
        open={Boolean(toast.message)}
        onClose={() => setToast({ success: false, message: null })}
        success={toast.success}
        autoHideDuration={8000}
        text={toast.message}
      />
    </>
  );
};

export default ProgressionAnalysisList;
